// Pages load as their own chunks, so a page only downloads its own code: an
// app's "Sign in with Cloudish" visitor gets the sign-in screens, not the
// home page's intro or the dashboard. Not React.lazy: that suspends on its
// first render even once its chunk is in, which a prerendered page can't
// afford — renderToString (entry-server.tsx) would write the fallback, and
// hydration (main.tsx) would start from a boundary still waiting. A page
// here renders straight away once preload() has resolved, and suspends to
// the nearest <Suspense> (App.tsx) only while its chunk is on the way.
//
// A chunk that won't load is almost always a tab opened before a deploy,
// asking for a file the new build no longer has: reload once to pick up the
// new build, and only fail the page if that doesn't help.

import type { ComponentType } from 'react';

const RELOADED_KEY = 'cloudish:chunk-reload';

export interface LazyPage {
  (): JSX.Element;
  preload: () => Promise<void>;
}

export function lazyPage(load: () => Promise<ComponentType>): LazyPage {
  let Loaded: ComponentType | null = null;
  let failure: unknown = null;
  let pending: Promise<void> | null = null;

  const preload = () =>
    (pending ??= load().then(
      (component) => {
        Loaded = component;
      },
      (err) => {
        failure = err;
        reloadOnce();
      }
    ));

  function Page() {
    if (Loaded) return <Loaded />;
    if (failure) throw failure;
    throw preload();
  }
  Page.preload = preload;
  return Page;
}

/** Pages that live in one chunk and are shown together (a layout and the
 *  pages in its <Outlet />): loading one readies them all, so moving
 *  between them never suspends again. */
export function lazyGroup<M>(load: () => Promise<M>) {
  const pages: LazyPage[] = [];
  let group: Promise<M> | null = null;
  const loadGroup = () => {
    if (!group) {
      group = load();
      group.then(() => pages.forEach((page) => page.preload()), () => {});
    }
    return group;
  };
  return (pick: (mod: M) => ComponentType) => {
    const page = lazyPage(() => loadGroup().then(pick));
    pages.push(page);
    return page;
  };
}

function reloadOnce() {
  if (typeof window === 'undefined') return;
  try {
    const last = Number(sessionStorage.getItem(RELOADED_KEY) || 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
}
